import {
  Breadcrumb,
  DocumentIcon,
  ErrorAlert,
  GiftIcon,
  Input,
  LinkIcon,
  Squares2X2Icon,
  useCallback,
  useMemo,
  useParams,
  useState,
} from "@";
import { DataTable } from "@/shared/components/tanstackTable";
import { affiliateLinkDetailsFilter } from "../../lib/helpers";
import {
  useAffiliateLinkClassificationsQuery,
  useAffiliateLinkGiftProjectsQuery,
  useAffiliateLinkLandingPagesQuery,
  useAffiliateLinkProjectsQuery,
} from "../../api/queries";
import showDetailColumns from "../../components/ShowDetailColumns";

const TABS = [
  { id: "projects", label: "روابط المشروعات", icon: LinkIcon },
  { id: "giftProjects", label: "روابط الإهداءات", icon: GiftIcon },
  { id: "classifications", label: "روابط التصنيفات", icon: Squares2X2Icon },
  { id: "landingPages", label: "روابط صفحات الهبوط", icon: DocumentIcon },
];

const EMPTY_SEARCHES = {
  projects: "",
  giftProjects: "",
  classifications: "",
  landingPages: "",
};

const Page = () => {
  const { id } = useParams();

  const links = [
    { name: "الروابط", link: "/affiliateLinks" },
    { name: "عرض الرابط", link: "" },
  ];

  const [activeTab, setActiveTab] = useState("projects");
  const [searches, setSearches] = useState(EMPTY_SEARCHES);

  // Each tab is fetched lazily: its React Query is only enabled while that tab
  // is active, so we don't hit every API on first load. Results stay cached
  // (staleTime: Infinity), so re-opening a tab doesn't trigger a refetch.
  const queries = {
    projects: useAffiliateLinkProjectsQuery(id, activeTab === "projects"),
    giftProjects: useAffiliateLinkGiftProjectsQuery(
      id,
      activeTab === "giftProjects",
    ),
    classifications: useAffiliateLinkClassificationsQuery(
      id,
      activeTab === "classifications",
    ),
    landingPages: useAffiliateLinkLandingPagesQuery(
      id,
      activeTab === "landingPages",
    ),
  };

  const activeQuery = queries[activeTab];
  const activeSearch = searches[activeTab];

  const handleSearchChange = useCallback(
    (e) => {
      const { value } = e.target;
      setSearches((prev) => ({ ...prev, [activeTab]: value }));
    },
    [activeTab],
  );

  const filtered = useMemo(
    () => affiliateLinkDetailsFilter(activeQuery.data, activeSearch),
    [activeQuery.data, activeSearch],
  );

  const hasRows = (activeQuery.data?.length || 0) > 0;

  return (
    <>
      <ErrorAlert reload={activeQuery.refetch} show={activeQuery.isError} />
      <Breadcrumb links={links} />

      {/* Tabs — pill bar matching FormTabs / verifications Tabs */}
      <nav className="relative mb-5">
        <div
          role="tablist"
          aria-label="أنواع الروابط"
          className="relative flex flex-nowrap items-center gap-1 overflow-x-auto whitespace-nowrap rounded-2xl border border-gray-100 bg-gradient-to-l from-purple-50/30 via-white to-purple-50/20 p-1.5 backdrop-blur-sm [-ms-overflow-style:none] [scrollbar-width:none] dark:border-gray-700/60 dark:from-purple-950/15 dark:via-gray-800 dark:to-purple-950/10 [&::-webkit-scrollbar]:hidden"
        >
          {TABS.map(({ id: tabId, label, icon: Icon }) => {
            const isActive = activeTab === tabId;
            const count = queries[tabId].data?.length;
            return (
              <button
                key={tabId}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveTab(tabId)}
                className={`group/tab relative inline-flex shrink-0 items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-medium tracking-tight transition-all duration-200 md:px-4 ${
                  isActive
                    ? "bg-gradient-to-br from-purple-800 to-purple-950 text-white shadow-md shadow-purple-900/25 ring-1 ring-purple-900/10 dark:from-purple-600 dark:to-purple-800"
                    : "text-gray-600 hover:bg-white hover:text-purple-900 hover:shadow-sm dark:text-gray-400 dark:hover:bg-gray-700/60 dark:hover:text-purple-300"
                }`}
              >
                <Icon
                  className={`h-4 w-4 shrink-0 transition-transform ${
                    isActive ? "" : "group-hover/tab:scale-110"
                  }`}
                />
                <span>{label}</span>
                {count != null && (
                  <span
                    className={`ms-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-bold tabular-nums ${
                      isActive
                        ? "bg-white/20 text-white ring-1 ring-white/30"
                        : "bg-gray-100 text-gray-600 ring-1 ring-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:ring-gray-600"
                    }`}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </nav>

      {/* Active tab content */}
      <div className="table-card" role="tabpanel">
        {hasRows && (
          <div>
            <Input
              type="search"
              value={activeSearch}
              style="single-search-input"
              placeholder="بحث بالاسم أو الرابط"
              change={handleSearchChange}
            />
          </div>
        )}
        <DataTable
          data={filtered}
          columns={showDetailColumns}
          isLoading={activeQuery.isLoading}
          localPg
        />
      </div>
    </>
  );
};

export default Page;
